
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCollectionDetail } from '../hooks/useCollections';
import BookCard from '../components/BookCard';
import BookCardList from '../components/BookCardList';
import ViewControls from '../components/ViewControls';
import { toPersianNumber } from '@/utils/persianNumber';
import { cn } from '@/utils/cn';

const COLLECTION_COLORS = [
    'bg-[#F8C8B8]',
    'bg-[#BFD7F2]',
    'bg-[#F3C4D2]',
    'bg-[#C7E8D2]',
    'bg-[#D9C8F2]',
    'bg-[#BFE7E1]',
];

const CollectionDetailsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const collectionId = Number(id);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const { data: collection, isLoading, error } = useCollectionDetail(collectionId);

    // Helper for loading/error states
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error || !collection) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-500 font-persian gap-4" dir="rtl">
                <div>خطایی رخ داده است</div>
                <button onClick={() => navigate(-1)} className="text-primary-600 font-bold">بازگشت</button>
            </div>
        );
    }

    const carouselBooks = collection.books.map((b) => ({
        book_id: b.book_id,
        title: b.title,
        author: b.author,
        cover_image_url: b.cover_image_url ?? null,
        topic: b.topic,
    }));

    const routeState = location.state as { collectionColor?: string } | null;
    const collectionColor = routeState?.collectionColor
        ?? COLLECTION_COLORS[Math.max(0, collectionId - 1) % COLLECTION_COLORS.length];
    const stackBooks = collection.books.length % 2 === 0
        ? collection.books.slice(0, -1)
        : collection.books;
    const coverSpread = stackBooks.length > 1 ? 64 / (stackBooks.length - 1) : 0;

    return (
        <div className={cn('min-h-screen -mb-20 pb-20', collectionColor)} dir="rtl">
            <div className="px-4 pt-4 pb-2 z-30 flex items-center">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 -mr-2 text-white hover:bg-black/10 rounded-full transition-colors"
                >
                    <ArrowRight className="w-6 h-6" />
                </button>
            </div>

            <div className="px-4 pb-12">
                <div className="flex flex-col items-center text-center mb-8 px-4">
                    {/* Minimal layered book-cover composition */}
                    <div className="relative mx-auto w-full max-w-[340px] h-[230px] mb-5 overflow-visible" aria-hidden="true">
                        {stackBooks.map((book, index) => {
                            const pair = Math.ceil(index / 2);
                            const side = index === 0 ? 0 : index % 2 === 1 ? -1 : 1;
                            const position = 50 + side * pair * coverSpread;
                            const rotation = side * (index === 0 ? 0 : 8 + pair * 2);
                            const compact = stackBooks.length > 9;

                            return (
                            <div
                                key={book.book_id}
                                className={cn('absolute top-0 -translate-x-1/2 rounded-xl overflow-hidden border border-white/70 bg-white/20 shadow-2xl transition-transform duration-300', compact ? 'w-20 h-[120px]' : 'w-[110px] h-[165px]')}
                                style={{
                                    left: `${position}%`,
                                    top: `${Math.min(pair * 7, 48)}px`,
                                    transform: `translateX(-50%) rotate(${rotation}deg)`,
                                    zIndex: stackBooks.length - index,
                                }}
                            >
                                {book.cover_image_url ? (
                                    <img
                                        src={book.cover_image_url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-white/40 to-black/30" />
                                )}
                            </div>
                            );
                        })}
                    </div>

                    <h1 className="text-2xl sm:text-3xl font-bold font-persian text-gray-800 mb-2 leading-tight">
                        {toPersianNumber(collection.name)}
                    </h1>

                    <p className="text-base sm:text-lg text-gray-800/90 font-persian leading-relaxed max-w-md">
                        {toPersianNumber(collection.description)}
                    </p>
                </div>

                {/* View Controls */}
                <div className="mb-4">
                    <ViewControls
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        showSort={false}
                        sortBy="newest"
                        onSortChange={() => { }}
                        className="py-0"
                    />
                </div>

                {/* Books List/Grid */}
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-x-5 sm:gap-x-6 md:gap-x-8 gap-y-6 bg-transparent">
                        {collection.books.map((book, index) => (
                            <div key={book.book_id} className="w-full">
                                <BookCard
                                    book={book}
                                    className="bg-transparent"
                                    titleClassName="text-gray-800"
                                    onClick={() =>
                                        navigate(`/library/book`, {
                                            state: {
                                                bookId: book.book_id,
                                                source: 'collection',
                                                collectionName: collection.name,
                                                books: carouselBooks,
                                                initialIndex: index,
                                            },
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {collection.books.map((book, index) => (
                            <BookCardList
                                key={book.book_id}
                                book={book}
                                className="bg-transparent hover:bg-transparent shadow-none"
                                titleClassName="text-gray-800"
                                authorClassName="text-gray-800"
                                onClick={() =>
                                    navigate(`/library/book`, {
                                        state: {
                                            bookId: book.book_id,
                                            source: 'collection',
                                            collectionName: collection.name,
                                            books: carouselBooks,
                                            initialIndex: index,
                                        },
                                    })
                                }
                            />
                        ))}
                    </div>
                )}


                {collection.books.length === 0 && (
                    <div className="text-center py-12 text-gray-400 font-persian text-sm">
                        هیچ کتابی در این مجموعه یافت نشد
                    </div>
                )}
            </div>
        </div>
    );
};

export default CollectionDetailsPage;
